import { add } from './documentStore';
import { countPages, imagesToPdf, PdfError } from './pdfEngine';
import { convertToPdf } from './api';
import type { StoredDocument } from './types';

/**
 * Turns dropped files into working documents, entirely in the browser where it
 * can be.
 *
 * PDFs are read as-is and JPEG/PNG are embedded locally, so both work with no
 * network. Office formats are the one case that has to reach the server,
 * because converting them needs LibreOffice.
 */

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

export interface IngestResult {
  documents: StoredDocument[];
  failed: Array<{ name: string; reason: string }>;
}

function extensionOf(name: string): string {
  const match = /\.([^.]+)$/.exec(name);
  return match?.[1]?.toLowerCase() ?? '';
}

function replaceExtension(name: string): string {
  return `${name.replace(/\.[^.]+$/, '')}.pdf`;
}

async function ingestOne(file: File, online: boolean): Promise<StoredDocument> {
  const extension = extensionOf(file.name);
  const bytes = new Uint8Array(await file.arrayBuffer());

  if (extension === 'pdf' || file.type === 'application/pdf') {
    return add({
      name: file.name,
      bytes,
      pageCount: await countPages(bytes),
      byteSize: bytes.byteLength,
      converted: false,
      sourceFormat: 'pdf',
    });
  }

  if (IMAGE_EXTENSIONS.has(extension) || /^image\/(jpeg|png)$/.test(file.type)) {
    const mimeType = extension === 'png' || file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const pdfBytes = await imagesToPdf([{ bytes, mimeType }]);
    return add({
      name: replaceExtension(file.name),
      bytes: pdfBytes,
      pageCount: 1,
      byteSize: pdfBytes.byteLength,
      converted: true,
      sourceFormat: extension || 'image',
    });
  }

  // Everything else needs LibreOffice, which only exists on the server.
  if (!online) {
    throw new PdfError(
      `"${file.name}" needs converting on the server, which is unreachable offline. PDFs and JPEG/PNG images still work.`,
    );
  }
  const pdfBytes = await convertToPdf(file);
  return add({
    name: replaceExtension(file.name),
    bytes: pdfBytes,
    pageCount: await countPages(pdfBytes),
    byteSize: pdfBytes.byteLength,
    converted: true,
    sourceFormat: extension,
  });
}

/** One bad file must not discard a whole multi-file drop. */
export async function ingestFiles(files: File[], online: boolean): Promise<IngestResult> {
  const documents: StoredDocument[] = [];
  const failed: IngestResult['failed'] = [];

  for (const file of files) {
    try {
      documents.push(await ingestOne(file, online));
    } catch (err) {
      failed.push({
        name: file.name,
        reason: err instanceof Error ? err.message : 'This file could not be processed.',
      });
    }
  }
  return { documents, failed };
}
